/**
 * A QR code, drawn here rather than installed.
 *
 * This exists for exactly one square: the `otpauth://` URI a phone scans when
 * somebody sets up two-step sign-in. That is a security screen, and a
 * dependency on a security screen is a supply chain that reaches the one
 * secret this product has no way to rotate quietly — so the encoder is in the
 * repo, where it can be read, rather than in `node_modules`, where it cannot.
 *
 * It is deliberately the smallest thing that does the job: byte mode, error
 * correction level M, versions 1 to 10. A TOTP URI runs to around 130
 * characters, which version 8 holds with room to spare, and nothing else asks
 * this for a QR code (the menu's printed codes are drawn by the server). It
 * lives here, shared, because two screens set up a second factor: the back
 * office's two-step sign-in and the platform admin's. Level M rather than L because this is read off
 * a screen by a phone held at an angle in a back office, and the extra
 * redundancy is what survives a reflection off the glass.
 *
 * If it is ever asked for more than it can hold it throws rather than
 * returning a square that will not scan. The screen that calls it falls back
 * to the typed secret, which is what a broken camera leaves you with anyway.
 *
 * Follows ISO/IEC 18004. The pieces are, in order: encode the text as
 * codewords, add Reed-Solomon correction, interleave the blocks, lay them into
 * the grid around the finder and timing patterns, then try all eight masks and
 * keep the one the standard's penalty rules like best.
 */

/** Level M, versions 1-10: total codewords, and how the data is blocked up. */
interface VersionSpec {
  /** Error-correction codewords per block. */
  ec: number;
  /** Blocks in the first group, and data codewords in each. */
  g1: number;
  d1: number;
  /** The second group, which most versions do not have. */
  g2: number;
  d2: number;
  /** Centres of the alignment patterns, both across and down. */
  align: readonly number[];
}

const VERSIONS: readonly VersionSpec[] = [
  { ec: 10, g1: 1, d1: 16, g2: 0, d2: 0, align: [] },
  { ec: 16, g1: 1, d1: 28, g2: 0, d2: 0, align: [6, 18] },
  { ec: 26, g1: 1, d1: 44, g2: 0, d2: 0, align: [6, 22] },
  { ec: 18, g1: 2, d1: 32, g2: 0, d2: 0, align: [6, 26] },
  { ec: 24, g1: 2, d1: 43, g2: 0, d2: 0, align: [6, 30] },
  { ec: 16, g1: 4, d1: 27, g2: 0, d2: 0, align: [6, 34] },
  { ec: 18, g1: 4, d1: 31, g2: 0, d2: 0, align: [6, 22, 38] },
  { ec: 22, g1: 2, d1: 38, g2: 2, d2: 39, align: [6, 24, 42] },
  { ec: 22, g1: 3, d1: 36, g2: 2, d2: 37, align: [6, 26, 46] },
  { ec: 26, g1: 4, d1: 43, g2: 1, d2: 44, align: [6, 28, 50] },
];

/* --- GF(256) --------------------------------------------------------------
   Reed-Solomon arithmetic over the field the standard names, with 0x11d as
   the primitive polynomial. Logs and antilogs are built once so that a
   multiply is two table lookups and an add. */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

for (let value = 1, power = 0; power < 255; power++) {
  EXP[power] = value;
  LOG[value] = power;
  value <<= 1;
  if (value & 0x100) value ^= 0x11d;
}
for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255] as number;

function mul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[(LOG[a] as number) + (LOG[b] as number)] as number;
}

/** The generator polynomial for `count` correction codewords. */
function generator(count: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < count; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] = (next[j] as number) ^ (poly[j] as number);
      next[j + 1] = (next[j + 1] as number) ^ mul(poly[j] as number, EXP[i] as number);
    }
    poly = next;
  }
  return poly;
}

/** The remainder of dividing the data by the generator: the correction block. */
function correction(data: Uint8Array, count: number): Uint8Array {
  const gen = generator(count);
  const working = new Uint8Array(data.length + count);
  working.set(data);
  for (let i = 0; i < data.length; i++) {
    const factor = working[i] as number;
    if (factor === 0) continue;
    for (let j = 0; j < gen.length; j++) {
      working[i + j] = (working[i + j] as number) ^ mul(gen[j] as number, factor);
    }
  }
  return working.slice(data.length);
}

/* --- Codewords ----------------------------------------------------------- */

/** The 15-bit format field: correction level and mask, BCH-protected. */
function formatBits(mask: number): number {
  // Level M is 0b00, which is why it does not appear in the shift below.
  const data = mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** The 18-bit version field, only drawn from version 7 upwards. */
function versionBits(version: number): number {
  let rem = version;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (version << 12) | rem;
}

function codewordsFor(text: string, spec: VersionSpec, version: number): Uint8Array {
  const bytes = new TextEncoder().encode(text);
  const dataCodewords = spec.g1 * spec.d1 + spec.g2 * spec.d2;

  const bits: number[] = [];
  const push = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };

  push(0b0100, 4); // Byte mode.
  push(bytes.length, version < 10 ? 8 : 16);
  for (const byte of bytes) push(byte, 8);

  // Terminator, then whatever it takes to reach a byte boundary, then the two
  // pad codewords the standard names, alternating.
  const capacity = dataCodewords * 8;
  for (let i = 0; i < 4 && bits.length < capacity; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  const out = new Uint8Array(dataCodewords);
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | (bits[i + j] as number);
    out[i / 8] = byte;
  }
  // The pad alternates from the first pad codeword, not from the start of the
  // block, which is why this counts from where the data ran out.
  const firstPad = bits.length / 8;
  for (let i = firstPad; i < dataCodewords; i++) {
    out[i] = (i - firstPad) % 2 === 0 ? 0xec : 0x11;
  }
  return out;
}

/**
 * Split into blocks, correct each, then interleave.
 *
 * The interleaving is the whole point of blocking: a thumbprint or a glare
 * spot destroys a contiguous run of modules, and spreading each block's
 * codewords across the square turns one unrecoverable hole into a few
 * recoverable ones in every block.
 */
function finalCodewords(data: Uint8Array, spec: VersionSpec): Uint8Array {
  const blocks: Uint8Array[] = [];
  const checks: Uint8Array[] = [];
  let at = 0;
  for (let i = 0; i < spec.g1 + spec.g2; i++) {
    const length = i < spec.g1 ? spec.d1 : spec.d2;
    const block = data.slice(at, at + length);
    at += length;
    blocks.push(block);
    checks.push(correction(block, spec.ec));
  }

  const out: number[] = [];
  const longest = Math.max(spec.d1, spec.d2);
  for (let i = 0; i < longest; i++) {
    for (const block of blocks) if (i < block.length) out.push(block[i] as number);
  }
  for (let i = 0; i < spec.ec; i++) {
    for (const check of checks) out.push(check[i] as number);
  }
  return Uint8Array.from(out);
}

/* --- The grid ------------------------------------------------------------ */

type Grid = Uint8Array[];

function drawFinder(grid: Grid, fixed: Grid, x: number, y: number): void {
  for (let dy = -1; dy <= 7; dy++) {
    for (let dx = -1; dx <= 7; dx++) {
      const px = x + dx;
      const py = y + dy;
      if (px < 0 || py < 0 || px >= grid.length || py >= grid.length) continue;
      const far = Math.max(Math.abs(dx - 3), Math.abs(dy - 3));
      (grid[py] as Uint8Array)[px] = far !== 2 && far !== 4 ? 1 : 0;
      (fixed[py] as Uint8Array)[px] = 1;
    }
  }
}

function buildGrid(version: number, spec: VersionSpec, codewords: Uint8Array): Grid {
  const size = 17 + 4 * version;
  const grid: Grid = Array.from({ length: size }, () => new Uint8Array(size));
  const fixed: Grid = Array.from({ length: size }, () => new Uint8Array(size));

  const set = (x: number, y: number, dark: boolean) => {
    (grid[y] as Uint8Array)[x] = dark ? 1 : 0;
    (fixed[y] as Uint8Array)[x] = 1;
  };

  drawFinder(grid, fixed, 0, 0);
  drawFinder(grid, fixed, size - 7, 0);
  drawFinder(grid, fixed, 0, size - 7);

  // Timing: the dashed lines that tell a scanner how wide a module is.
  for (let i = 8; i < size - 8; i++) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }

  // Alignment: five-module targets, except where they would sit on a finder.
  for (const cy of spec.align) {
    for (const cx of spec.align) {
      const onFinder =
        (cx === 6 && cy === 6) ||
        (cx === 6 && cy === size - 7) ||
        (cx === size - 7 && cy === 6);
      if (onFinder) continue;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }

  // Reserve the format field so data does not land in it, and the one module
  // that is always dark. Index 6 is skipped in both directions: that module
  // belongs to the timing line, which crosses the format field and must not be
  // overwritten by it.
  for (let i = 0; i < 9; i++) {
    if (i === 6) continue;
    set(8, i, false);
    set(i, 8, false);
  }
  for (let i = 0; i < 8; i++) {
    set(size - 1 - i, 8, false);
    set(8, size - 1 - i, false);
  }
  set(8, size - 8, true);

  if (version >= 7) {
    const bits = versionBits(version);
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) === 1;
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      set(a, b, dark);
      set(b, a, dark);
    }
  }

  // The data snakes up and down in two-module columns, right to left, stepping
  // over the vertical timing line.
  let bit = 0;
  let upwards = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let step = 0; step < size; step++) {
      for (let across = 0; across < 2; across++) {
        const x = right - across;
        const y = upwards ? size - 1 - step : step;
        if ((fixed[y] as Uint8Array)[x] === 1) continue;
        const byte = codewords[bit >>> 3];
        // Past the end of the data are the remainder bits, which are light.
        (grid[y] as Uint8Array)[x] = byte === undefined ? 0 : (byte >>> (7 - (bit & 7))) & 1;
        bit++;
      }
    }
    upwards = !upwards;
  }

  const best = chooseMask(grid, fixed);
  applyMask(grid, fixed, best);
  drawFormat(grid, best);
  return grid;
}

function maskAt(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
  }
}

function applyMask(grid: Grid, fixed: Grid, mask: number): void {
  const size = grid.length;
  for (let y = 0; y < size; y++) {
    const row = grid[y] as Uint8Array;
    const fixedRow = fixed[y] as Uint8Array;
    for (let x = 0; x < size; x++) {
      if (fixedRow[x] === 1) continue;
      if (maskAt(mask, x, y)) row[x] = (row[x] as number) ^ 1;
    }
  }
}

function drawFormat(grid: Grid, mask: number): void {
  const size = grid.length;
  const bits = formatBits(mask);
  const at = (i: number) => ((bits >>> i) & 1) as number;

  for (let i = 0; i <= 5; i++) (grid[i] as Uint8Array)[8] = at(i);
  (grid[7] as Uint8Array)[8] = at(6);
  (grid[8] as Uint8Array)[8] = at(7);
  (grid[8] as Uint8Array)[7] = at(8);
  for (let i = 9; i < 15; i++) (grid[8] as Uint8Array)[14 - i] = at(i);

  for (let i = 0; i < 8; i++) (grid[8] as Uint8Array)[size - 1 - i] = at(i);
  for (let i = 8; i < 15; i++) (grid[size - 15 + i] as Uint8Array)[8] = at(i);
  (grid[size - 8] as Uint8Array)[8] = 1;
}

/**
 * Try all eight masks and keep the least bad.
 *
 * The four penalties in the standard each describe a way a square confuses a
 * scanner: long same-coloured runs, solid blocks, runs that look like a finder
 * pattern, and an overall balance too far from half dark.
 */
function chooseMask(grid: Grid, fixed: Grid): number {
  let best = 0;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    applyMask(grid, fixed, mask);
    drawFormat(grid, mask);
    const score = penalty(grid);
    applyMask(grid, fixed, mask);
    if (score < bestScore) {
      bestScore = score;
      best = mask;
    }
  }
  return best;
}

function penalty(grid: Grid): number {
  const size = grid.length;
  let score = 0;

  const runScore = (line: number[]) => {
    let total = 0;
    let run = 1;
    for (let i = 1; i < line.length; i++) {
      if (line[i] === line[i - 1]) {
        run++;
      } else {
        if (run >= 5) total += 3 + (run - 5);
        run = 1;
      }
    }
    if (run >= 5) total += 3 + (run - 5);

    // The finder-lookalike: 1:1:3:1:1 with four light modules to one side.
    const text = line.join('');
    for (const pattern of ['10111010000', '00001011101']) {
      let from = text.indexOf(pattern);
      while (from !== -1) {
        total += 40;
        from = text.indexOf(pattern, from + 1);
      }
    }
    return total;
  };

  for (let y = 0; y < size; y++) {
    score += runScore(Array.from(grid[y] as Uint8Array));
  }
  for (let x = 0; x < size; x++) {
    score += runScore(
      Array.from({ length: size }, (_, y) => (grid[y] as Uint8Array)[x] as number),
    );
  }

  for (let y = 0; y < size - 1; y++) {
    for (let x = 0; x < size - 1; x++) {
      const first = (grid[y] as Uint8Array)[x];
      if (
        first === (grid[y] as Uint8Array)[x + 1] &&
        first === (grid[y + 1] as Uint8Array)[x] &&
        first === (grid[y + 1] as Uint8Array)[x + 1]
      ) {
        score += 3;
      }
    }
  }

  let dark = 0;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) dark += (grid[y] as Uint8Array)[x] as number;
  }
  const away = Math.abs((dark * 100) / (size * size) - 50);
  score += Math.floor(away / 5) * 10;

  return score;
}

/**
 * The square, as rows of booleans. True is a dark module.
 *
 * No quiet zone: the border is four modules wide and belongs to whatever draws
 * this, because that is where the background colour is decided.
 */
export function qrModules(text: string): boolean[][] {
  for (let version = 1; version <= VERSIONS.length; version++) {
    const spec = VERSIONS[version - 1] as VersionSpec;
    const capacity = (spec.g1 * spec.d1 + spec.g2 * spec.d2) * 8;
    const needed = 4 + (version < 10 ? 8 : 16) + new TextEncoder().encode(text).length * 8;
    if (needed > capacity) continue;
    const grid = buildGrid(
      version,
      spec,
      finalCodewords(codewordsFor(text, spec, version), spec),
    );
    return grid.map((row) => Array.from(row, (module) => module === 1));
  }
  throw new Error('Too long for a QR code this encoder can draw');
}
